import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { getCategories } from '../../../lib/api';
import { useManagerPortalConfig } from '../manager/useManagerPortalConfig';
import { ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';

export default function AdminArticleArrangement({ embedded = false }) {
  const { portalConfig, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [categories, setCategories] = useState([]);
  const [layout, setLayout] = useState({ order: [], hiddenIds: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!portalConfig) return;
    setLayout({
      order: Array.isArray(portalConfig?.posTerminal?.categoryLayout?.order)
        ? portalConfig.posTerminal.categoryLayout.order
        : [],
      hiddenIds: Array.isArray(portalConfig?.posTerminal?.categoryLayout?.hiddenIds)
        ? portalConfig.posTerminal.categoryLayout.hiddenIds
        : [],
    });
  }, [portalConfig]);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const orderedCategories = useMemo(() => {
    const orderIndex = new Map((layout.order || []).map((id, idx) => [id, idx]));
    return categories
      .map((category, naturalIndex) => ({
        category,
        rank: orderIndex.has(category.id) ? orderIndex.get(category.id) : 1000 + naturalIndex,
      }))
      .sort((a, b) => a.rank - b.rank)
      .map(({ category }) => category);
  }, [categories, layout.order]);

  const toggleHidden = (categoryId) => {
    const nextHidden = new Set(layout.hiddenIds || []);
    if (nextHidden.has(categoryId)) nextHidden.delete(categoryId);
    else nextHidden.add(categoryId);
    setLayout({ ...layout, hiddenIds: Array.from(nextHidden) });
  };

  const moveCategory = (categoryId, direction) => {
    const ids = orderedCategories.map((category) => category.id);
    const from = ids.indexOf(categoryId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return;
    const next = [...ids];
    [next[from], next[to]] = [next[to], next[from]];
    setLayout({ ...layout, order: next });
  };

  const save = async () => {
    setSaving(true);
    await savePortalConfig(
      (prev) => ({
        ...prev,
        posTerminal: {
          ...prev?.posTerminal,
          categoryLayout: layout,
        },
      }),
      'Anordnung der Kategorien gespeichert',
    );
    setSaving(false);
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'min-h-[220px]' : 'min-h-[60vh]'}`}>
        <div className="rounded-3xl border border-slate-200 bg-white px-6 py-5 text-sm text-slate-500 shadow-sm">
          Lade POS-Konfiguration…
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? 'space-y-6' : 'space-y-6 p-6'}`}>
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-400">Artikel</p>
        <h1 className="text-3xl font-bold text-[#132d39]">Anordnung</h1>
        <p className="max-w-3xl text-slate-600">
          Bestimmen Sie die Reihenfolge der Artikelkategorien im POS-Menü.
          Versteckte Kategorien werden nicht mehr im linken Category-Rail des POS-Terminals angezeigt.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-bold tracking-tight text-[#314b59]">Kategorienreihenfolge</CardTitle>
            <p className="text-sm text-slate-500">Ziehen Sie Kategorien in die gewünschte Reihenfolge oder verwenden Sie die Pfeiltasten.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {orderedCategories.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Keine Kategorien gefunden.
              </div>
            ) : (
              <div className="space-y-2">
                {orderedCategories.map((category, index) => {
                  const isHidden = layout.hiddenIds.includes(category.id);
                  return (
                    <div key={category.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{category.name}</p>
                        <p className="text-xs text-slate-500">{isHidden ? 'Versteckt' : 'Sichtbar'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => moveCategory(category.id, -1)}
                          disabled={index === 0}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveCategory(category.id, 1)}
                          disabled={index === orderedCategories.length - 1}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleHidden(category.id)}
                          className="inline-flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700"
                        >
                          {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          {isHidden ? 'Anzeigen' : 'Verstecken'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-bold tracking-tight text-[#314b59]">POS-Vorschau</CardTitle>
            <p className="text-sm text-slate-500">Die Reihenfolge der sichtbaren Kategorien bestimmt die Menünavigation im POS.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">Sichtbare Kategorien</p>
              <div className="mt-3 space-y-2">
                {orderedCategories.filter((category) => !layout.hiddenIds.includes(category.id)).map((category) => (
                  <div key={category.id} className="rounded-2xl bg-white px-3 py-2 text-slate-700 shadow-sm">{category.name}</div>
                ))}
                {orderedCategories.filter((category) => !layout.hiddenIds.includes(category.id)).length === 0 && (
                  <p className="text-slate-500">Keine sichtbaren Kategorien konfiguriert.</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={save} className="rounded-2xl bg-[#71c545] text-white hover:bg-[#63b13b]" disabled={saving}>
          {saving ? 'Speichert…' : 'Speichern'}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setLayout({
              order: Array.isArray(portalConfig?.posTerminal?.categoryLayout?.order)
                ? portalConfig.posTerminal.categoryLayout.order
                : [],
              hiddenIds: Array.isArray(portalConfig?.posTerminal?.categoryLayout?.hiddenIds)
                ? portalConfig.posTerminal.categoryLayout.hiddenIds
                : [],
            });
          }}
        >
          Zurücksetzen
        </Button>
      </div>
    </div>
  );
}
